import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage, 
  type Facility
} from '../../utils/facilityStorage';
import { formatShipmentType } from '../../utils/shipmentHelpers';
import { incomingDockLockKey, type IncomingDockMove } from '../../utils/incomingDockMoves';

import { getAuthToken } from '../../utils/cookieUtils';
// Add GraphQL API URL constant
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Add GraphQL mutations for dock management
const UPDATE_DOCK_STATUS_MUTATION = `
  mutation UpdateDockStatus($uuid: String!, $input: DockStatusUpdateInput!) {
    update_dock_status(uuid: $uuid, input: $input) {
      uuid
      current_status
      status
      updated_at
    }
  }
`;

const UPDATE_DOCK_ASSIGNMENT_MUTATION = `
  mutation UpdateDockAssignment($uuid: String!, $input: DockAssignmentUpdateInput!) {
    update_dock_assignment(uuid: $uuid, input: $input) {
      uuid
      assigned_driver_id
      driver_name
      trailer_number
      shipment_number
      bol_id
      updated_at
    }
  }
`;

const UPDATE_DOCK_GROUP_MUTATION = `
  mutation UpdateDockGroup($uuid: String!, $input: DockGroupUpdateInput!) {
    update_dock_group(uuid: $uuid, input: $input) {
      uuid
      group
      direction
      updated_at
    }
  }
`;

const ADD_DOCK_NOTE_MUTATION = `
  mutation AddDockNote($input: DockNoteInput!) {
    add_dock_note(input: $input) {
      uuid
      dock_uuid
      note
      created_at
      updated_at
    }
  }
`;

const UPDATE_DOCK_NOTE_MUTATION = `
  mutation UpdateDockNote($uuid: String!, $input: DockNoteUpdateInput!) {
    update_dock_note(uuid: $uuid, input: $input) {
      uuid
      note
      updated_at
    }
  }
`;

const MOVE_TO_YARD_MUTATION = `
  mutation MoveToYard($input: MoveToYardInput!) {
    move_to_yard(input: $input) {
      success
      message
      yard_spot
      updated_at
    }
  }
`;

const UPDATE_LOADING_STATUS_MUTATION = `
  mutation UpdateLoadingStatus($input: LoadingStatusUpdateInput!) {
    update_loading_status(input: $input) {
      uuid
      current_status
      updated_at
    }
  }
`;

const SEND_TO_YARD_MUTATION = `
  mutation SendToYard($input: SendToYardInput!) {
    send_to_yard(input: $input) {
      success
      message
      updated_at
    }
  }
`;

const UPDATE_DOCK_CONFIGURATION_MUTATION = `
  mutation UpdateDockConfiguration($facility_id: ID!, $input: DockConfigurationInput!) {
    update_dock_configuration(facility_id: $facility_id, input: $input) {
      facility_id
      configuration
      updated_at
    }
  }
`;

// Facility configuration query is now part of the combined query in useDockData hook

// Types for dock doors and activities
export type ScheduledTime = {
  start: string;
  end: string;
  shipmentId?: string;
};

export type Status = 'empty' | 'loading' | 'loaded' | 'unloading' | 'unloaded';

export type SpotGroup = 'inbound' | 'outbound' | 'unassigned' | 'assigned';

export type DockType = 'inbound' | 'outbound' | 'mixed' | 'maintenance' | 'staging' | 'unassigned';

// Types for yard data (imported from yard module)
export type YardStatus = 'available' | 'in-use' | 'inactive';

export type Zone = {
  id: string;
  name: string;
  color: string;
  zone_type?: string;
};

export type YardSpot = {
  id: string;
  number: number;
  displayNumber: string;
  zoneId: string;
  status: YardStatus;
  driverId: string | null;
  driverName: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  trailerType?: 'dry_van' | 'reefer' | 'flatbed' | 'tanker' | 'container';
  shipmentNumber: string | null;
  bol_id?: string | null;
  carrier_name?: string | null;
  scheduledTimes?: any[];
  current_status?: string | null;
  direction?: string | null;
  order_number?: number | null;
};

const INCOMING_DOCK_MOVES_QUERY = `
  query IncomingDockMoves($facilityId: ID!) {
    incoming_dock_moves(facility_id: $facilityId) {
      dock_uuid
      task_uuid
      trailer_uuid
      trailer_number
      carrier_name
      trailer_status_name
      shipment_reference_number
    }
  }
`;

export interface DockDoor {
  id: string;
  number: string;
  status: Status;
  group: SpotGroup;
  dockType: DockType;
  current_status: string;
  driverName: string | null;
  assignedDriverId: string | null;
  driverCheckInTime?: string | null;
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
    truck?: {
      truckNumber: string;
      truck_number: string;
    };
  } | null;
  trailerNumber: string | null;
  trailerUuid: string | null;
  shipmentNumber: string | null;
  referenceNumber: string | null;
  direction: string | null;
  shipment_type: string | null;
  scheduledTimes: ScheduledTime[];
  mdt: string;
  detention: string;
  dock_timer: string;
  // Original datetime values for displaying actual times
  mdtActualTime?: string | null;
  detentionActualTime?: string | null;
  bol_id: string | null;
  carrier_name?: string;
  appointment_date?: string;
  reference_number?: string | null;
  note?: string | null;
  order_number?: number;
  zoneName?: string;
  warehouseZoneUuid?: string;
  trailerLineageNotes?: string | null;
  trailerLineageUuid?: string | null;
  lastMovedAt?: string | null;
  trailerStatus?: string | null;
  configurationStatus?: 'available' | 'unavailable' | null; // Status from dock configuration
  notes?: string | null; // Notes from dock configuration
  paused?: boolean; // Paused status - true means paused, false/null/undefined means not paused
}

export type HistoryEntry = {
  driverId: string;
  driverName: string;
  arrivalTime: string;
  departureTime: string;
  duration: number; // in minutes
  activity: 'load' | 'unload' | 'both';
  shipmentId: string;
};

export type DockHistory = {
  [dockId: string]: HistoryEntry[];
};

// Types for pending changes tracking
export type PendingDockChanges = {
  [dockId: string]: {
    status?: Status;
    current_status?: string;
    group?: SpotGroup;
    assignedDriverId?: string | null;
    driverName?: string | null;
    trailerNumber?: string | null;
    shipmentNumber?: string | null;
    note?: string | null;
    bol_id?: string | null;
    paused?: boolean;
    isNew?: boolean;
  };
};

export type PendingFacilityChanges = {
  configuration?: {
    dock?: {
      colorConfig?: any;
      thresholds?: any;
      lastUpdated?: string;
    };
  };
};

export type TimerColorConfig = {
  warning: string;
  critical: string;
  good: string;
  warningThreshold: number;
  criticalThreshold: number;
  textColor: string;
};

export type CardColorConfig = {
  default: string;
  oneWarning: string;
  twoWarnings: string;
  threeWarnings: string;
  oneCritical: string;
  twoCriticals: string;
  threeCriticals: string;
  mixed: string;
  textColor: string;
};

export type ColorConfig = {
  mdt: TimerColorConfig;
  detention: TimerColorConfig;
  dockTimer: TimerColorConfig;
  card: CardColorConfig;
};

// API response type
interface ApiDockSlot {
  id: string;
  number: string;
  current_status?: string;
  status?: string;
  group?: string;
  direction?: string;
  assigned_driver_id?: string | null;
  dock_number?: string;
  dock_door?: string;
  driver_id?: number;
  driver_name?: string;
  reference_number?: string;
  trailer_number?: string;
  shipment_type?: string;
  scheduled_times?: {
    start: string;
    end: string;
    shipment_id?: string;
  }[];
  history?: {
    driver_id: string;
    driver_name: string;
    arrival_time: string;
    departure_time: string;
    duration: number;
    activity: string;
    shipment_id: string;
  }[];
  mdt?: string;
  detention?: string;
  dock_timer?: string;
  bol_id?: string | null;
  carrier_name?: string;
  appointment_date?: string;
  note?: string | null;
}

// Convert API data to our DockDoor format
const mapApiDockSlotToDockDoor = (slot: ApiDockSlot): DockDoor => {
  // Extract just the number part from dock_number (e.g., "D7" from "DOCK:D7" or just "D7")
  const doorNumber = slot.dock_number || 
                     (slot.dock_door && slot.dock_door.includes(':') ? 
                      slot.dock_door.split(':')[1] : slot.dock_door) || 
                     slot.number;
  
  // Determine if this door has an assigned driver, trailer, or shipment
  const hasAssignedDriver = !!(slot.driver_id || slot.assigned_driver_id);
  const hasTrailer = !!slot.trailer_number;
  const hasShipment = !!slot.reference_number;
  const isInUse = hasAssignedDriver || hasTrailer || hasShipment;
  
  // Determine the group - if a driver is assigned, mark as 'assigned'
  // Or use the direction as the group if provided
  let group: SpotGroup = 'unassigned';
  if (slot.group) {
    group = slot.group as SpotGroup;
  } else if (slot.direction === 'inbound') {
    group = 'inbound';
  } else if (slot.direction === 'outbound') {
    group = 'outbound';
  } else if (isInUse) {
    group = 'assigned';
  }
  
  return {
    id: slot.id,
    number: doorNumber,
    current_status: slot.current_status || '',
    status: (slot.status as Status) || (isInUse ? 'in-use' : 'available'),
    group: group,
    dockType: 'unassigned', // Default for old API, will be overridden by GraphQL data
    assignedDriverId: slot.assigned_driver_id || (slot.driver_id?.toString() || null),
    driverName: slot.driver_name || null,
    driverCheckInTime: null, // Not available in old API
    shipmentNumber: slot.reference_number || null,
    referenceNumber: null, // Will be set from GraphQL data
    direction: slot.direction || null,
    shipment_type: slot.shipment_type || null,
    trailerNumber: slot.trailer_number || null,
    trailerUuid: null, // Not available in old API
    scheduledTimes: (slot.scheduled_times || []).map(time => ({
      start: time.start,
      end: time.end,
      shipmentId: time.shipment_id
    })),
    bol_id: slot.bol_id || null,
    mdt: slot.mdt || '',
    detention: slot.detention || '',
    dock_timer: slot.dock_timer || '',
    // Original datetime values not available in old API
    mdtActualTime: null,
    detentionActualTime: null,
    carrier_name: slot.carrier_name || undefined,
    appointment_date: slot.appointment_date || undefined,
    reference_number: slot.reference_number || null,
    note: slot.note || null,
    paused: false, // Default to not paused for legacy API data
  };
};

// Convert API history data to our HistoryEntry format
const mapApiHistoryToHistoryEntry = (entry: any): HistoryEntry => {
  return {
    driverId: entry.driver_id,
    driverName: entry.driver_name,
    arrivalTime: entry.arrival_time,
    departureTime: entry.departure_time,
    duration: entry.duration,
    activity: entry.activity as 'load' | 'unload' | 'both',
    shipmentId: entry.shipment_id
  };
};

// GraphQL API functions
export const saveDockChanges = async (
  pendingDockChanges: PendingDockChanges,
  pendingFacilityChanges: PendingFacilityChanges,
  facilityId?: number
): Promise<void> => {
  // Get auth token from cookie
  const authToken = getAuthToken();

  // Process each pending dock change
  for (const [dockId, changes] of Object.entries(pendingDockChanges)) {
    try {
      // Update dock status if changed
      if (changes.status !== undefined || changes.current_status !== undefined) {
        const input = {
          status: changes.status,
          current_status: changes.current_status
        };

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: UPDATE_DOCK_STATUS_MUTATION,
            variables: {
              uuid: dockId,
              input
            }
          })
        });

        const result = await response.json();
        if (result.errors) {
          console.error('Error updating dock status:', result.errors);
          throw new Error(result.errors[0].message);
        }
      }

      // Update dock assignment if changed
      if (changes.assignedDriverId !== undefined || changes.driverName !== undefined || 
          changes.trailerNumber !== undefined || changes.shipmentNumber !== undefined || 
          changes.bol_id !== undefined) {
        const input = {
          assigned_driver_id: changes.assignedDriverId,
          driver_name: changes.driverName,
          trailer_number: changes.trailerNumber,
          shipment_number: changes.shipmentNumber,
          bol_id: changes.bol_id
        };

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: UPDATE_DOCK_ASSIGNMENT_MUTATION,
            variables: {
              uuid: dockId,
              input
            }
          })
        });

        const result = await response.json();
        if (result.errors) {
          console.error('Error updating dock assignment:', result.errors);
          throw new Error(result.errors[0].message);
        }
      }

      // Update dock group if changed
      if (changes.group !== undefined) {
        const input = {
          group: changes.group,
          direction: changes.group === 'inbound' ? 'inbound' : 
                    changes.group === 'outbound' ? 'outbound' : null
        };

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: UPDATE_DOCK_GROUP_MUTATION,
            variables: {
              uuid: dockId,
              input
            }
          })
        });

        const result = await response.json();
        if (result.errors) {
          console.error('Error updating dock group:', result.errors);
          throw new Error(result.errors[0].message);
        }
      }

      // Update or add dock note if changed
      if (changes.note !== undefined) {
        const mutation = changes.isNew ? ADD_DOCK_NOTE_MUTATION : UPDATE_DOCK_NOTE_MUTATION;
        const input = changes.isNew ? {
          dock_uuid: dockId,
          note: changes.note
        } : {
          note: changes.note
        };

        const variables = changes.isNew ? { input } : { uuid: dockId, input };

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: mutation,
            variables
          })
        });

        const result = await response.json();
        if (result.errors) {
          console.error('Error updating dock note:', result.errors);
          throw new Error(result.errors[0].message);
        }
      }

    } catch (error) {
      console.error(`Error updating dock ${dockId}:`, error);
      throw error;
    }
  }

  // Process pending facility changes
  if (Object.keys(pendingFacilityChanges).length > 0 && facilityId) {
    try {
      const input = {
        configuration: JSON.stringify(pendingFacilityChanges.configuration)
      };

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: UPDATE_DOCK_CONFIGURATION_MUTATION,
          variables: {
            facility_id: facilityId,
            input
          }
        })
      });

      const result = await response.json();
      if (result.errors) {
        console.error('Error updating facility configuration:', result.errors);
        throw new Error(result.errors[0].message);
      }
    } catch (error) {
      console.error('Error updating facility configuration:', error);
      throw error;
    }
  }
};

// Helper function for dock actions
export const performDockAction = async (
  dockId: string,
  action: string,
  additionalData?: any
): Promise<any> => {
  // Get auth token from cookie
  const authToken = getAuthToken();

  let mutation: string;
  let input: any;

  switch (action) {
    case 'move_to_yard':
      mutation = MOVE_TO_YARD_MUTATION;
      input = {
        bol_id: additionalData.bol_id,
        yard_spot: additionalData.yard_spot,
        reference_number: additionalData.reference_number,
        trailer_number: additionalData.trailer_number
      };
      break;

    case 'update_loading_status':
      mutation = UPDATE_LOADING_STATUS_MUTATION;
      input = {
        shipment_id: dockId,
        status: additionalData.status
      };
      break;

    case 'send_to_yard':
      mutation = SEND_TO_YARD_MUTATION;
      input = {
        shipment_id: dockId
      };
      break;

    default:
      throw new Error(`Unknown action: ${action}`);
  }

  try {
    const response = await fetch(GRAPHQL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        query: mutation,
        variables: { input }
      })
    });

    const result = await response.json();
    if (result.errors) {
      console.error(`Error performing action ${action}:`, result.errors);
      throw new Error(result.errors[0].message);
    }

    return result.data;
  } catch (error) {
    console.error(`Error performing dock action ${action}:`, error);
    throw error;
  }
};

// Custom hook to fetch dock data
export const useDockData = (facilityId?: number) => {
  const [loading, setLoading] = useState(false);
  const [dockDoors, setDockDoors] = useState<DockDoor[]>([]);
  const [dockHistory, setDockHistory] = useState<DockHistory>({});
  const [error, setError] = useState<string | null>(null);
  const [facilities, setFacilities] = useState<{ id: number; name: string }[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<{ id: number; name: string } | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });
  const [timeInterval, setTimeInterval] = useState(12); // Default 12 hours
  const [colorConfig, setColorConfig] = useState<ColorConfig>({
    mdt: {
      warning: '#f97316',
      critical: '#ef4444',
      good: '#22c55e',
      warningThreshold: 30,
      criticalThreshold: 60,
      textColor: '#ffffff'
    },
    detention: {
      warning: '#f97316',
      critical: '#ef4444',
      good: '#22c55e',
      warningThreshold: 30,
      criticalThreshold: 60,
      textColor: '#ffffff'
    },
    dockTimer: {
      warning: '#f97316',
      critical: '#ef4444',
      good: '#22c55e',
      warningThreshold: 30,
      criticalThreshold: 60,
      textColor: '#ffffff'
    },
    card: {
      default: '#ffffff',
      oneWarning: '#fef3c7',
      twoWarnings: '#fde68a',
      threeWarnings: '#fcd34d',
      oneCritical: '#fee2e2',
      twoCriticals: '#fecaca',
      threeCriticals: '#fca5a5',
      mixed: '#f3f4f6',
      textColor: '#1f2937'
    }
  });

  // Add state for tracking changes
  const [pendingDockChanges, setPendingDockChanges] = useState<PendingDockChanges>({});
  const [pendingFacilityChanges, setPendingFacilityChanges] = useState<PendingFacilityChanges>({});
  const [savingChanges, setSavingChanges] = useState(false);

  // Yard data states
  const [yardSpots, setYardSpots] = useState<YardSpot[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [facilityZones, setFacilityZones] = useState<any[]>([]);

  // Lock-based state for tracking locked doors and yard spots
  const [taskBasedLockedDoors, setTaskBasedLockedDoors] = useState<Set<string>>(new Set());
  const [taskBasedLockedSpots, setTaskBasedLockedSpots] = useState<Set<string>>(new Set());
  const [activeLocks, setActiveLocks] = useState<any[]>([]);

  // Incoming trailer per door uuid, for the backfill strip
  const [incomingByDoor, setIncomingByDoor] = useState<Record<string, IncomingDockMove>>({});
  // Lock key the strip data was last fetched for; null until the first poll lands
  const incomingKeyRef = useRef<string | null>(null);
  // Drops a response that a facility switch or a newer fetch has overtaken
  const incomingRequestRef = useRef(0);

  // Staged data for deferred updates when modals are open
  const [stagedDockDoors, setStagedDockDoors] = useState<DockDoor[] | null>(null);
  const [stagedYardSpots, setStagedYardSpots] = useState<YardSpot[] | null>(null);
  const [stagedZones, setStagedZones] = useState<Zone[] | null>(null);
  const [stagedFacilityZones, setStagedFacilityZones] = useState<any[] | null>(null);
  const [stagedLocks, setStagedLocks] = useState<{
    lockedDoors: Set<string>;
    lockedSpots: Set<string>;
    activeLocks: any[];
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isModalOpenRef = useRef(false);

  // Track which resources (doors/spots) the user is actively viewing/editing
  const [activeResourceIds, setActiveResourceIds] = useState<Set<string>>(new Set());
  const activeResourceIdsRef = useRef<Set<string>>(new Set());

  // Fetch facilities
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, clear it and set default
            if (facilityId) {
              const facility = result.data.facilities.find((f: any) => f.id === facilityId);
              if (facility) {
                setSelectedFacility(facility);
                saveFacilityToStorage(facility);
              }
            } else if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
              saveFacilityToStorage(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (facilityId) {
            const facility = result.data.facilities.find((f: any) => f.id === facilityId);
            if (facility) {
              setSelectedFacility(facility);
              saveFacilityToStorage(facility);
            }
          } else if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
            saveFacilityToStorage(result.data.facilities[0]);
          }
        }
      } catch (err) {
        setError('Failed to fetch facilities');
        console.error(err);
      }
    };
    
    fetchFacilities();
  }, [facilityId]);

  // Initialize base dock doors when facility changes
  useEffect(() => {
    if (!selectedFacility) {
      setDockDoors([]);
      return;
    }
    
    // Initialize with empty array, will be populated by GraphQL query
    setDockDoors([]);
  }, [selectedFacility]);

  // Keep refs in sync with state
  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  useEffect(() => {
    activeResourceIdsRef.current = activeResourceIds;
  }, [activeResourceIds]);

  // Fetch dock data when facility changes
  useEffect(() => {
    if (!selectedFacility) return;

    // One request for every door's incoming trailer, flattened server side
    // into a single query rather than resolved per door.
    const fetchIncomingDockMoves = async () => {
      const requestId = ++incomingRequestRef.current;

      try {
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify({
            query: INCOMING_DOCK_MOVES_QUERY,
            variables: { facilityId: String(selectedFacility.id) }
          })
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        if (requestId !== incomingRequestRef.current) return;

        const byDoor: Record<string, IncomingDockMove> = {};
        (result.data?.incoming_dock_moves || []).forEach((move: IncomingDockMove) => {
          if (move.dock_uuid) byDoor[move.dock_uuid] = move;
        });
        setIncomingByDoor(byDoor);
      } catch (err) {
        console.error('Failed to fetch incoming dock moves:', err);
        // Matches no lock key, so the next poll tries again
        if (requestId === incomingRequestRef.current) incomingKeyRef.current = 'retry';
      }
    };

    incomingKeyRef.current = null;
    setIncomingByDoor({});
    
    const fetchDockData = async (forceApply: boolean = false) => {
      // Only show loading on manual/forced refreshes, not on background polls
      if (forceApply) {
        setLoading(true);
      }
      setError(null);
      
      try {
        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // GraphQL query for combined warehouse, yard, and facility data
        const combinedQuery = `
          query GetWarehouseYardAndFacilityData {
            warehouse(facilityId: "${selectedFacility ? selectedFacility.id : "1"}") {
              facilityId
              warehouseZones {
                zoneUuid
                zoneName
                configuration
                docks {
                  dockUuid
                  dockName
                  dockStatus
                  order_number
                  trailer_name
                  trailer_uuid
                  carrier_name
                  configuration
                  
                  trailer{
                    active_lineage {
                      last_moved_at
                      uuid
                      notes
                    }
                    status {
                      uuid
                      name
                    }
                  }
                  shipment {
                    id
                    referenceNumber
                    direction
                    driver_assignment {
                      created_at
                    }
                    metadata
                  }
                   

                    driver {
                      id
                      firstName
                      lastName
                      truck {
                        truckNumber
                        truck_number
                      }
                    }
                }
              }
            }
            yard(facility_id: "${selectedFacility ? selectedFacility.id : "1"}") {
              facility_id
              facility_zones {
                facility_zone_name
                facility_zone_uuid
                zone_type
                configuration
                trailers {
                  trailerNumber
                  carrier{
                    name
                  }
                }
                yard_spots {
                  yard_spot_uuid
                  yard_spot_name
                  yard_spot_facility_zone_uuid
                  yard_spot_status
                  trailer_lineage_uuid
                  trailer_state_uuid
                  trailer_name
                  trailer_uuid
                  carrier_name
                  shipment_id
                  shipment_reference_number
                  driver_id
                  driver_name
                  order_number
                }
              }
              zoneSummary {
                facilityZoneName
                facilityZoneUuid
                totalYardSpots
              }
              statusSummary {
                available_yard_spots
                empty_trailers
                loaded_inbound
                loaded_outbound
              }
            }
            facility(id: "${selectedFacility ? selectedFacility.id : "1"}") {
              id
              configuration
            }
            facilityLocks(facilityId: "${selectedFacility ? selectedFacility.id : "1"}") {
              reason
              resource_uuid
              model_name
              operation_type
              created_by
              task_uuid
              created_at
              expires_at
              namespace
              facility_id
              resource {
                ... on YardSpotObject {
                  __typename
                  uuid
                  name
                }
                ... on DockObject {
                  __typename
                  uuid
                  name
                }
                ... on Trailer {
                  __typename
                  uuid
                  trailerNumber
                }
              }
            }
          }
        `;

        // Fetch combined warehouse and yard data using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: combinedQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        // Debug: Log the GraphQL response
        console.log("GraphQL Combined Response (Warehouse + Yard + Facility):", result.data);
        
        // Process the warehouse data to extract dock doors
        if (result.data && result.data.warehouse) {
          const warehouseData = result.data.warehouse;
          const extractedDockDoors: DockDoor[] = [];
          
          // Extract facility timezone from configuration (default to US/Eastern)
          let facilityTimezone = 'US/Eastern';
          if (result.data.facility?.configuration) {
            try {
              const config = typeof result.data.facility.configuration === 'string'
                ? JSON.parse(result.data.facility.configuration)
                : result.data.facility.configuration;
              facilityTimezone = config.timezone || 'US/Eastern';
            } catch (error) {
              console.error('Error parsing facility configuration for timezone:', error);
            }
          }
          console.log(`Using facility timezone: ${facilityTimezone} for MDT/detention calculations`);
          
          // Sort warehouse zones alphabetically by name
          const sortedZones = [...warehouseData.warehouseZones].sort((a, b) => 
            a.zoneName.localeCompare(b.zoneName)
          );
          
          // Process each warehouse zone and its docks
          sortedZones.forEach((zone: any) => {
            if (zone.docks && zone.docks.length > 0) {
              zone.docks.forEach((dock: any) => {
                // Check if dock has any active assignments
                const hasTrailer = !!dock.trailer_name;
                const hasCarrier = !!dock.carrier_name;
                const isInUse = hasTrailer || hasCarrier;
                
                // Map the dock data to our DockDoor format
                // Get dock type from zone configuration - only use what's explicitly configured
                const dockType: DockType = (zone.configuration?.dockType as DockType) || 'unassigned';
                
                // Log for debugging
                console.log(`Processing dock ${dock.dockName} in zone ${zone.zoneName}:`, {
                  zoneConfig: zone.configuration,
                  zoneDockType: zone.configuration?.dockType,
                  finalDockType: dockType
                });

                // Get last moved at time from trailer lineage
                const lastMovedAt = dock.trailer?.active_lineage?.last_moved_at || null;
                
                // Calculate dock timer based on last_moved_at if available, otherwise use default
                let dockTimer = "";
                if (isInUse) {
                  if (lastMovedAt) {
                    dockTimer = calculateElapsedTime(lastMovedAt);
                  } else {
                    dockTimer = "00:00:00";
                  }
                }

                // Calculate MDT and detention times from shipment metadata
                let mdt = "";
                let detention = "";
                let mdtActualTime = null;
                let detentionActualTime = null;
                let appointmentDate = null;
                
                if (isInUse && dock.shipment?.metadata) {
                  try {
                    const metadata = typeof dock.shipment.metadata === 'string' 
                      ? JSON.parse(dock.shipment.metadata) 
                      : dock.shipment.metadata;
                    
                    // Calculate MDT from appointment_must_depart_by_date
                    if (metadata.appointment_must_depart_by_date) {
                      mdt = calculateCountdownTime(metadata.appointment_must_depart_by_date, facilityTimezone);
                      mdtActualTime = metadata.appointment_must_depart_by_date;
                    }
                    
                    // Calculate detention from detention_time
                    if (metadata.detention_time) {
                      detention = calculateCountdownTime(metadata.detention_time, facilityTimezone);
                      detentionActualTime = metadata.detention_time;
                    }
                    if(metadata.appointment_date){
                      appointmentDate = metadata.appointment_date;
                    }
                  } catch (error) {
                    console.error('Error parsing shipment metadata:', error);
                  }
                }

                const mappedDock: DockDoor = {
                  id: dock.dockUuid,
                  number: dock.dockName,
                  current_status: dock.trailer?.status?.name  || (isInUse ? 'in-use' : 'available'),
                  status: mapDockStatusToStatus(dock.dockStatus, isInUse),
                  group: inferGroupFromDockType(dock.dockType, zone.zoneName),
                  dockType: dockType,
                  assignedDriverId: dock.driver?.id || null,
                  driverName: dock.driver ? `${dock.driver.firstName || ''} ${dock.driver.lastName || ''}`.trim() || null : null,
                  driverCheckInTime: dock.shipment?.driver_assignment?.created_at || null,
                  driver: dock.driver ? {
                    id: dock.driver.id,
                    firstName: dock.driver.firstName,
                    lastName: dock.driver.lastName,
                    truck: dock.driver.truck ? {
                      truckNumber: dock.driver.truck.truckNumber,
                      truck_number: dock.driver.truck.truck_number
                    } : undefined
                  } : null,
                  shipmentNumber: dock.shipment?.id || null,
                  referenceNumber: dock.shipment?.referenceNumber || null,
                  direction: dock.shipment?.direction || null,
                  shipment_type: formatShipmentType(dock.shipment?.metadata?.shipment_type) || null,
                  trailerNumber: dock.trailer_name || null,
                  trailerUuid: dock.trailer_uuid || null,
                  scheduledTimes: [],
                  bol_id: null,
                  // Use calculated timers from shipment metadata
                  mdt: mdt,
                  detention: detention,
                  dock_timer: dockTimer,
                  // Store original datetime values
                  mdtActualTime: mdtActualTime,
                  detentionActualTime: detentionActualTime,
                  carrier_name: dock.carrier_name || undefined,
                  appointment_date: appointmentDate,
                  reference_number: null, // order_number is just display order, not reference_number
                  note: null,
                  order_number: dock.order_number ? parseInt(dock.order_number, 10) : undefined,
                  zoneName: zone.zoneName,
                  warehouseZoneUuid: zone.zoneUuid,
                  trailerLineageNotes: dock.trailer?.active_lineage?.notes || null,
                  trailerLineageUuid: dock.trailer?.active_lineage?.uuid || null,
                  lastMovedAt: lastMovedAt,
                  trailerStatus: dock.trailer?.status?.name || null,
                  configurationStatus: dock.configuration?.status || null, // Get status from dock configuration
                  notes: dock.configuration?.notes || null, // Get notes from dock configuration
                  paused: dock.configuration?.paused || false, // Get paused status from dock configuration
                };
                
                extractedDockDoors.push(mappedDock);
              });
            }
          });
          
          // Store extracted dock doors for later application
          const newDockDoors = extractedDockDoors.length > 0 ? extractedDockDoors : dockDoors;
          
          // If modal is open and this is a background refresh, stage the data
          if (isModalOpenRef.current && !forceApply) {
            setStagedDockDoors(newDockDoors);
          } else {
            // Apply immediately if no modal or forced refresh
            setDockDoors(newDockDoors);
            setStagedDockDoors(null);
          }
        } else {
          // No warehouse data found, keep existing base doors
          console.log('No warehouse data found for facility:', selectedFacility.id);
        }

        // Process the yard data
        if (result.data && result.data.yard) {
          const yardData = result.data.yard;
          
          // Extract and process zones from the GraphQL response
          if (yardData.facility_zones && yardData.facility_zones.length > 0) {
            // Build the zones from GraphQL data
            const graphQLZones = yardData.facility_zones.map((zone: any) => ({
              id: zone.facility_zone_uuid,
              name: zone.facility_zone_name,
              color: zone.configuration?.color || 
                     (zone.zone_type === "staging" ? "#F59E0B" : "#2196F3"), // Use config color, or amber for staging, or blue as default
              zone_type: zone.zone_type
            }));
            
            // Store zones for later application
            const newZones = graphQLZones;
            
            // Process all yard spots from all zones
            const allSpots: YardSpot[] = [];
            
            yardData.facility_zones.forEach((zone: any) => {
              if (zone.yard_spots && zone.yard_spots.length > 0) {
                zone.yard_spots.forEach((spot: any) => {
                  // Determine spot status from GraphQL data
                  let status: YardStatus = 'available';
                  
                  // Set status based on trailer information
                  if (spot.trailer_uuid || spot.yard_spot_status === 'OCCUPIED' || spot.yard_spot_status === 'in-use') {
                    status = 'in-use';
                  } else if (spot.yard_spot_status === 'INACTIVE' || spot.yard_spot_status === 'inactive') {
                    status = 'inactive';
                  }
                  
                  // Determine direction
                  let direction: string | null = null;
                  
                  if (spot.yard_spot_status === "EMPTY" || spot.yard_spot_status === "IN USE") {
                    direction = "outbound"; // Empty trailer (IN USE = empty with a shipment attached)
                  } else if (spot.trailer_state_uuid && spot.trailer_uuid) {
                    direction = "inbound"; // Loaded trailer
                  }
                  
                  // Map API spot to our internal YardSpot type
                  allSpots.push({
                    id: spot.yard_spot_uuid,
                    number: parseInt(spot.yard_spot_name.replace(/\D/g, '')) || 0,
                    displayNumber: spot.yard_spot_name,
                    zoneId: zone.facility_zone_uuid,
                    status,
                    driverId: spot.driver_id || null,
                    driverName: spot.driver_name || null,
                    trailerId: spot.trailer_uuid || null,
                    trailerNumber: spot.trailer_name || null,
                    shipmentNumber: spot.shipment_reference_number || null,
                    bol_id: spot.shipment_id || null,
                    carrier_name: spot.carrier_name || null,
                    current_status: spot.yard_spot_status || null,
                    direction,
                    order_number: spot.order_number ? parseInt(spot.order_number, 10) : null,
                    scheduledTimes: [] // Initialize with empty scheduledTimes array
                  });
                });
              }
            });
            
            // Sort spots within each zone by order_number
            const sortedSpots = allSpots.sort((a, b) => {
              // First sort by zone
              if (a.zoneId !== b.zoneId) {
                return a.zoneId.localeCompare(b.zoneId);
              }
              // Then sort by order_number within the same zone
              // Handle null/undefined values by treating them as very high numbers (put them at the end)
              const aOrderNumber = (a.order_number !== null && a.order_number !== undefined) ? a.order_number : Number.MAX_SAFE_INTEGER;
              const bOrderNumber = (b.order_number !== null && b.order_number !== undefined) ? b.order_number : Number.MAX_SAFE_INTEGER;
              return aOrderNumber - bOrderNumber;
            });
            
            // Store yard data for later application
            const newYardSpots = sortedSpots;
            const newFacilityZones = yardData.facility_zones;
            
            // If modal is open and this is a background refresh, stage the data
            if (isModalOpenRef.current && !forceApply) {
              setStagedYardSpots(newYardSpots);
              setStagedZones(newZones);
              setStagedFacilityZones(newFacilityZones);
            } else {
              // Apply immediately if no modal or forced refresh
              setYardSpots(newYardSpots);
              setZones(newZones);
              setFacilityZones(newFacilityZones);
              setStagedYardSpots(null);
              setStagedZones(null);
              setStagedFacilityZones(null);
            }
          }
        } else {
          console.log('No yard data found for facility:', selectedFacility.id);
        }

        // Process facility configuration
        if (result.data && result.data.facility && result.data.facility.configuration) {
          const config = typeof result.data.facility.configuration === 'string'
            ? JSON.parse(result.data.facility.configuration)
            : result.data.facility.configuration;

          if (config.dock?.colorConfig) {
            console.log('Loading saved color configuration in DockManagement:', config.dock.colorConfig);
            setColorConfig(config.dock.colorConfig);
          }
        }

        // Process facility locks to determine locked dock doors and yard spots
        if (result.data && result.data.facilityLocks) {
          const locks = result.data.facilityLocks;

          // The load-time fetch already covers the first poll; after that,
          // refetch only when the set of incoming tasks changes.
          const incomingKey = incomingDockLockKey(locks);
          if (incomingKeyRef.current === null) {
            incomingKeyRef.current = incomingKey;
          } else if (incomingKey !== incomingKeyRef.current) {
            incomingKeyRef.current = incomingKey;
            if (incomingKey === '') {
              incomingRequestRef.current++;
              setIncomingByDoor({});
            } else {
              fetchIncomingDockMoves();
            }
          }
          const lockedDockIds = new Set<string>();
          const lockedYardSpotIds = new Set<string>();
          const lockedTrailerIds = new Set<string>();
          const activeLocks: any[] = [];

          locks.forEach((lock: any) => {
            // Check if this lock applies to a dock door
            const isDockLock = lock.model_name === 'DockObject' || 
                              (lock.resource && lock.resource.__typename === 'DockObject');
            
            // Check if this lock applies to a yard spot
            const isYardSpotLock = lock.model_name === 'YardSpotObject' || 
                                  (lock.resource && lock.resource.__typename === 'YardSpotObject');
            
            // Check if this lock applies to a trailer
            const isTrailerLock = lock.model_name === 'Trailer' || 
                                 (lock.resource && lock.resource.__typename === 'Trailer');
            
            if (isDockLock) {
              // Store the lock information for linking to tasks
              activeLocks.push(lock);
              
              // Add the dock UUID to locked doors
              const dockUuid = lock.resource_uuid || lock.resource?.uuid;
              if (dockUuid) {
                lockedDockIds.add(dockUuid);
              }
            } else if (isYardSpotLock) {
              // Store the lock information for linking to tasks
              activeLocks.push(lock);
              
              // Add the yard spot UUID to locked spots
              const yardSpotUuid = lock.resource_uuid || lock.resource?.uuid;
              if (yardSpotUuid) {
                lockedYardSpotIds.add(yardSpotUuid);
              }
            } else if (isTrailerLock) {
              // Store the lock information for linking to tasks
              activeLocks.push(lock);
              
              // Add the trailer UUID to locked trailers
              const trailerUuid = lock.resource_uuid || lock.resource?.uuid;
              if (trailerUuid) {
                lockedTrailerIds.add(trailerUuid);
              }
            }
          });

          // Cross-reference locked trailers with yard spots - if a trailer is locked, also lock the yard spot containing it
          if (result.data && result.data.yard && result.data.yard.facility_zones) {
            result.data.yard.facility_zones.forEach((zone: any) => {
              if (zone.yard_spots && zone.yard_spots.length > 0) {
                zone.yard_spots.forEach((spot: any) => {
                  const spotTrailerUuid = spot.trailer_uuid;
                  if (spotTrailerUuid && lockedTrailerIds.has(spotTrailerUuid)) {
                    lockedYardSpotIds.add(spot.yard_spot_uuid);
                    console.log(`Locking yard spot ${spot.yard_spot_name} (${spot.yard_spot_uuid}) because it contains locked trailer ${spot.trailer_name} (${spotTrailerUuid})`);
                  }
                });
              }
            });
          }

          // Store lock data for later application
          const newLockData = {
            lockedDoors: lockedDockIds,
            lockedSpots: lockedYardSpotIds,
            activeLocks: activeLocks
          };
          
          // If modal is open and this is a background refresh, stage the data
          if (isModalOpenRef.current && !forceApply) {
            setStagedLocks(newLockData);
          } else {
            // Apply immediately if no modal or forced refresh
            setTaskBasedLockedDoors(lockedDockIds);
            setTaskBasedLockedSpots(lockedYardSpotIds);
            setActiveLocks(activeLocks);
            setStagedLocks(null);
          }
        }

        // Reset pending changes when loading new data
        setPendingDockChanges({});
        setPendingFacilityChanges({});
      } catch (err) {
        setError('Failed to fetch dock data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    // Fetch immediately with force apply (show loading), the incoming
    // trailers alongside it rather than after it
    fetchDockData(true);
    fetchIncomingDockMoves();
    
    // Set up periodic refresh (every 30 seconds)
    const intervalId = setInterval(() => {
      console.log('Background refresh triggered');
      fetchDockData(false); // Background refresh without showing loading
    }, 30000); // 30 seconds
    
    // Cleanup interval on unmount or facility change
    return () => {
      clearInterval(intervalId);
    };
  }, [selectedFacility]); // Only depend on selectedFacility - isModalOpen is used internally but shouldn't trigger refetch

  // Apply staged data when modal closes with smart differential merging
  useEffect(() => {
    if (!isModalOpen && (stagedDockDoors || stagedYardSpots || stagedZones || stagedFacilityZones || stagedLocks)) {
      console.log('Modal closed, applying staged data with smart merge');
      const activeIds = activeResourceIdsRef.current;

      // Helper to check if data actually changed using deep comparison
      const hasChanged = (current: any, staged: any): boolean => {
        return JSON.stringify(current) !== JSON.stringify(staged);
      };

      if (stagedDockDoors) {
        // Smart merge: keep current state for resources being actively edited,
        // update all others from staged data
        const mergedDoors = stagedDockDoors.map(stagedDoor => {
          if (activeIds.has(stagedDoor.id)) {
            // Keep current data for actively edited door
            const currentDoor = dockDoors.find(d => d.id === stagedDoor.id);
            return currentDoor || stagedDoor;
          }
          return stagedDoor;
        });

        // Only update if data actually changed
        if (hasChanged(dockDoors, mergedDoors)) {
          console.log('Applying updated dock doors (excluding actively edited)');
          setDockDoors(mergedDoors);
        }
        setStagedDockDoors(null);
      }

      if (stagedYardSpots) {
        // Smart merge for yard spots
        const mergedSpots = stagedYardSpots.map(stagedSpot => {
          if (activeIds.has(stagedSpot.id)) {
            // Keep current data for actively edited spot
            const currentSpot = yardSpots.find(s => s.id === stagedSpot.id);
            return currentSpot || stagedSpot;
          }
          return stagedSpot;
        });

        // Only update if data actually changed
        if (hasChanged(yardSpots, mergedSpots)) {
          console.log('Applying updated yard spots (excluding actively edited)');
          setYardSpots(mergedSpots);
        }
        setStagedYardSpots(null);
      }

      if (stagedZones) {
        // Zones typically aren't edited in modals, so we can apply them directly
        if (hasChanged(zones, stagedZones)) {
          console.log('Applying updated zones');
          setZones(stagedZones);
        }
        setStagedZones(null);
      }

      if (stagedFacilityZones) {
        // Facility zones typically aren't edited in modals
        if (hasChanged(facilityZones, stagedFacilityZones)) {
          console.log('Applying updated facility zones');
          setFacilityZones(stagedFacilityZones);
        }
        setStagedFacilityZones(null);
      }

      if (stagedLocks) {
        // Locks should be updated immediately as they affect UI state
        // but they don't disrupt user input since they're just visual indicators
        console.log('Applying updated locks');
        setTaskBasedLockedDoors(stagedLocks.lockedDoors);
        setTaskBasedLockedSpots(stagedLocks.lockedSpots);
        setActiveLocks(stagedLocks.activeLocks);
        setStagedLocks(null);
      }

      // Clear active resource tracking after applying staged data
      setActiveResourceIds(new Set());
    }
  }, [isModalOpen, stagedDockDoors, stagedYardSpots, stagedZones, stagedFacilityZones, stagedLocks, dockDoors, yardSpots, zones, facilityZones]);

  // Facility configuration is now fetched as part of the combined query above

  // Helper function to calculate elapsed time from last_moved_at to current time
const calculateElapsedTime = (lastMovedAt: string): string => {
  try {
    const lastMovedDate = new Date(lastMovedAt);
    const currentDate = new Date();
    
    // Calculate the difference in milliseconds
    const diffMs = currentDate.getTime() - lastMovedDate.getTime();
    
    // Convert to total seconds
    const totalSeconds = Math.floor(diffMs / 1000);
    
    // Calculate hours, minutes, and seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    // Format as HH:MM:SS
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error calculating elapsed time:', error);
    return '00:00:00';
  }
};

// Helper function to calculate countdown time (target date - now)
const calculateCountdownTime = (targetTime: string, facilityTimezone: string = 'US/Eastern'): string => {
  try {
    // The timestamps are in local facility time but formatted as UTC
    // We need to parse them as local time, not UTC
    
    // Remove timezone suffix and parse as local time
    const cleanTimeString = targetTime.replace(/(\+00:00|Z)$/, '');
    
    // Parse as local time by creating date without timezone info
    const target = new Date(cleanTimeString);
    const now = new Date();
    
    // Calculate difference (positive = time remaining, negative = overdue)
    const diffMs = target.getTime() - now.getTime();
    
    const isNegative = diffMs < 0;
    const absDiffMs = Math.abs(diffMs);
    
    // Convert to total seconds
    const totalSeconds = Math.floor(absDiffMs / 1000);
    
    // Calculate hours, minutes, and seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
    // Debug logging
    console.log(`Countdown calculation for ${targetTime}:`, {
      originalTime: targetTime,
      cleanedTime: cleanTimeString,
      target: target.toISOString(),
      now: now.toISOString(),
      diffMs,
      result: isNegative ? `-${timeString}` : timeString
    });
    
    return isNegative ? `-${timeString}` : timeString;
  } catch (error) {
    console.error('Error calculating countdown time:', error);
    return '00:00:00';
  }
};

// Helper function to map dock status to our Status type
const mapDockStatusToStatus = (dockStatus: string, isInUse?: boolean): Status => {
  switch (dockStatus?.toLowerCase()) {
    case 'in-use':
    case 'in_use':
    case 'occupied':
      return 'loading'; // Dock is actively being used for loading/unloading
    case 'available':
      return isInUse ? 'loading' : 'empty'; // If trailer present, show as loading; otherwise available
    case 'loading':
      return 'loading';
    case 'unloading':
      return 'unloading';
    case 'loaded':
      return 'loaded';
    default:
      // If no explicit status but trailer present, show as loading
      return isInUse ? 'loading' : 'empty';
  }
};

  // Helper function to infer group from dock type and zone name
  const inferGroupFromDockType = (dockType: string, zoneName: string): SpotGroup => {
    if (dockType) {
      switch (dockType.toLowerCase()) {
        case 'inbound':
          return 'inbound';
        case 'outbound':
          return 'outbound';
        default:
          break;
      }
    }
    
    // Fallback to zone name inference
    const lowerZoneName = zoneName?.toLowerCase() || '';
    if (lowerZoneName.includes('receiving') || lowerZoneName.includes('inbound')) {
      return 'inbound';
    } else if (lowerZoneName.includes('shipping') || lowerZoneName.includes('outbound')) {
      return 'outbound';
    }
    
    return 'unassigned';
  };

  // Function to get pending changes count
  const getPendingChangesCount = () => {
    return Object.keys(pendingDockChanges).length + Object.keys(pendingFacilityChanges).length;
  };

  // Function to save all pending changes
  const saveAllChanges = async () => {
    setSavingChanges(true);
    try {
      await saveDockChanges(pendingDockChanges, pendingFacilityChanges, selectedFacility?.id);
      
      // Clear pending changes after successful save
      setPendingDockChanges({});
      setPendingFacilityChanges({});
      
      // Refresh data
      setTimeInterval(prev => prev + 1);
    } catch (error) {
      console.error('Error saving dock changes:', error);
      throw error;
    } finally {
      setSavingChanges(false);
    }
  };

  // Helper function to find locks affecting a specific dock door
  const getLocksForDockDoor = (dockDoorId: string) => {
    return activeLocks.filter((lock: any) => {
      const lockResourceUuid = lock.resource_uuid || lock.resource?.uuid;
      
      // Check if lock directly targets this dock door
      if (lockResourceUuid === dockDoorId) {
        return true;
      }
      
      return false;
    });
  };

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacilityWithStorage = (facility: { id: number; name: string } | null) => {
    setSelectedFacility(facility);
    saveFacilityToStorage(facility);
  };

  // Check if there's staged data waiting to be applied
  const hasStagedData = !!(
    stagedDockDoors ||
    stagedYardSpots ||
    stagedZones ||
    stagedFacilityZones ||
    stagedLocks
  );

  // Helper to compare doors while excluding time-based fields that constantly update
  const compareDoorsIgnoringTimers = (door1: DockDoor, door2: DockDoor): boolean => {
    // Create copies without the constantly-updating timer fields
    const { mdt: _, detention: __, dock_timer: ___, ...door1WithoutTimers } = door1;
    const { mdt: ____, detention: _____, dock_timer: ______, ...door2WithoutTimers } = door2;

    return JSON.stringify(door1WithoutTimers) !== JSON.stringify(door2WithoutTimers);
  };

  // Smart function to check if staged data affects a specific resource
  const hasStagedDataForResource = (resourceId: string): boolean => {
    if (!hasStagedData) {
      console.log(`[hasStagedDataForResource] No staged data at all`);
      return false;
    }

    console.log(`[hasStagedDataForResource] Checking resource ${resourceId}`);

    // Check if the staged data contains changes to this specific resource
    if (stagedDockDoors) {
      const currentDoor = dockDoors.find(d => d.id === resourceId);
      const stagedDoor = stagedDockDoors.find(d => d.id === resourceId);

      console.log(`[hasStagedDataForResource] Door check - currentDoor exists: ${!!currentDoor}, stagedDoor exists: ${!!stagedDoor}`);

      // Only show overlay if this specific door has different data (ignoring timers)
      if (currentDoor && stagedDoor) {
        const isDifferent = compareDoorsIgnoringTimers(currentDoor, stagedDoor);
        console.log(`[hasStagedDataForResource] Door ${resourceId} is different (ignoring timers): ${isDifferent}`);
        if (isDifferent) {
          return true;
        }
      }
    }

    if (stagedYardSpots) {
      const currentSpot = yardSpots.find(s => s.id === resourceId);
      const stagedSpot = stagedYardSpots.find(s => s.id === resourceId);

      console.log(`[hasStagedDataForResource] Spot check - currentSpot exists: ${!!currentSpot}, stagedSpot exists: ${!!stagedSpot}`);

      // Only show overlay if this specific spot has different data
      if (currentSpot && stagedSpot) {
        const isDifferent = JSON.stringify(currentSpot) !== JSON.stringify(stagedSpot);
        console.log(`[hasStagedDataForResource] Spot ${resourceId} is different: ${isDifferent}`);
        if (isDifferent) {
          return true;
        }
      }
    }

    console.log(`[hasStagedDataForResource] No changes detected for ${resourceId}`);
    return false;
  };

  return {
    loading,
    dockDoors,
    dockHistory,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility: setSelectedFacilityWithStorage,
    timeInterval,
    setTimeInterval,
    colorConfig,
    setColorConfig,
    // New GraphQL-related exports
    pendingDockChanges,
    setPendingDockChanges,
    pendingFacilityChanges,
    setPendingFacilityChanges,
    savingChanges,
    getPendingChangesCount,
    saveAllChanges,
    performDockAction,
    // Yard data exports
    yardSpots,
    zones,
    facilityZones,
    // Task-based locks
    taskBasedLockedDoors,
    taskBasedLockedSpots,
    activeLocks,
    getLocksForDockDoor,
    incomingByDoor,
    // Modal state for deferred updates
    isModalOpen,
    setIsModalOpen,
    hasStagedData,
    hasStagedDataForResource,
    // Active resource tracking for smart merging
    setActiveResourceIds
  };
}; 